import {isNotNullOrUndefined} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import $ from 'jquery';
import React, {ReactNode, useRef} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as contextmenu from 'components/ContextMenu';
import {SEARCH_RESULT_COLOR, SELECTED_SEARCH_RESULT_COLOR} from 'notebook/mediocre/search/SearchConstants';
import {
  EMPTY_SEARCH_RESULT_PROPS,
  getSearchResultsAtRoot,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import columnDefinitionToTooltip from 'notebook/structured-tables/columns/columnDefinitionToTooltip';
import * as RowConstants from 'notebook/tables/constants/RowConstants';
import type {ColumnConstraint, TooltipIcon} from 'notebook/tables/constants/TableColumnConstants';
import {
  SelectionRange,
  TABLE_COLUMN,
  TABLE_ROW,
  TableDimension,
} from 'notebook/tables/constants/TableSelectionConstants';
import TableAxisCellDragHandle from 'notebook/tables/core-ui/TableAxisCellDragHandle';
import {
  AxisCellContextMenuTableContext,
  AxisCellDimActions,
  useAxisCellContextMenu,
  useAxisCellContextMenuTableContext,
} from 'notebook/tables/core-ui/useAxisCellContextMenu';
import excess from 'notebook/tables/excess';
import {countTableRender} from 'notebook/tables/perf/TableInstrumentation';
import {StandaloneAxisCellModelProvider} from 'notebook/tables/store/contexts/AxisCellModelContext';
import {useAxisCellModel} from 'notebook/tables/store/hooks/useAxisCellModel';
import {TableMutationMethods, useTableMutations} from 'notebook/tables/store/hooks/useTableMutations';
import {isColumnHeaderModel, isRowHeaderModel} from 'notebook/tables/store/models/AxisCellModel';
import {ColumnHeaderModel} from 'notebook/tables/store/models/ColumnHeaderModel';
import {RowHeaderModel} from 'notebook/tables/store/models/RowHeaderModel';
import TableUtil from 'notebook/tables/TableUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {BLUE_LIGHT} from 'ui-library/Colors';
import {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {BasicEvent, ElementMouseEvent} from 'util/Types';

export type AxisType = TableDimension;

/** Screen-reader-only text announced on a column header whose column is locked. */
export const LOCKED_COLUMN_DESCRIPTION = 'Locked column';

const ARCHIVED_ROW_TOOLTIP_FIRST_LINE = 'Archived';
export const UNSUBMITTED_CHANGES_TOOLTIP = 'Please submit table'; // exported for test
const TOMBSTONE_REASON_TOOLTIP = 'Row is invalid. Please adjust any formulas before deleting row.';
const ROW_ERROR_TOOLTIP = 'Error in row';

export interface TableAxisCellProps {
  cellDragStart?: (
    options: {start: [number, number] | null; active: string},
    event: React.MouseEvent
  ) => void;
  cellDragOver?: (index: [number, number] | null, e: React.MouseEvent) => void;
  /**
   * Overrides {@link AxisHeaderModel.showDragHandle} for a Single Input header, whose drag
   * capability is owned by `TableSingleInputGroup` rather than by the table.
   */
  showDragHandle?: boolean;
  dragHandleMouseDown?: (draggingDimension: TableDimension, index: number) => boolean;
  /**
   * Context menu options the cell component itself contributes, listed after the ones the table
   * contributes through `TableEditableProps.getExtraContextMenuOptions`. Only for options that
   * cannot be derived from the table model, as `UnitsTableAxisCell`'s unit list cannot because it
   * is fetched.
   */
  additionalContextMenuOptions?: Array<contextmenu.ContextMenuOption>;
  finalizeAndExitEditMode?: () => void;
  dataTestAttrs?: DataTestAttrs;

  // Has the row been in the view port of the browser yet?
  hasRowBeenInView?: boolean;

  freezeThroughColumn?: (j: number) => void;
  unfreezeFromColumn?: (j: number) => void;
}

// ---------------------------------------------------------------------------
// Pure utility functions (exported for direct use and testability)
// ---------------------------------------------------------------------------

export function isInSelectionRange(
  selectionRange: SelectionRange | null,
  index: number | undefined
): boolean {
  if (selectionRange != null && index !== undefined) {
    return selectionRange.minIndex <= index && index <= selectionRange.maxIndex;
  }
  return false;
}

export function getStartIndex(dim: AxisType, index: number): [number, number] {
  switch (dim) {
    case TABLE_ROW:
      return [index, 0];
    case TABLE_COLUMN:
      return [0, index];
    default:
      assert(false, `Unexpected dimension ${dim}`);
  }
}

/**
 * We only show the "excess label", e.g. "A" or "1" if we are in "formula mode" (showExcessLabels),
 * or there is no header text (customLabel == null).
 */
export function shouldHideExcessLabels(showExcessLabels: boolean, customLabel: ReactNode): boolean {
  return !showExcessLabels && customLabel != null;
}

export function getExcessLabel(dim: AxisType, index: number): string {
  return dim === TABLE_ROW
    ? String(excess.util.rowIndexToNumber(index))
    : excess.util.colNumberToLetters(index);
}

export function getTooltipTitle(
  dim: AxisType,
  columnConstraint: ColumnConstraint | null,
  tooltipIcons: Array<TooltipIcon> | null | undefined,
  rowProperties: RowConstants.RowProperties | null | undefined
): string {
  if (dim === TABLE_COLUMN) {
    let tooltips: Array<string | null | undefined> = [columnConstraint?.tooltip];
    if (columnConstraint?.columnDefinition) {
      tooltips.push(columnDefinitionToTooltip(columnConstraint.columnDefinition));
    }

    const allTooltipIcons = (tooltipIcons || columnConstraint?.tooltipIcons) ?? [];
    tooltips = tooltips.concat(allTooltipIcons.map(({tooltip}) => tooltip));

    return tooltips.filter((option) => isNotNullOrUndefined(option)).join(', ');
  } else {
    if (rowProperties?.archivePurpose != null) {
      return `${ARCHIVED_ROW_TOOLTIP_FIRST_LINE}\n${rowProperties.archivePurpose}`;
    } else if (rowProperties?.isTombstoned) {
      return rowProperties?.tombstoneReasonTooltip || TOMBSTONE_REASON_TOOLTIP;
    } else if (!rowProperties?.hasErrors && rowProperties?.hasUnsubmittedChanges) {
      return rowProperties?.unsubmittedChangesTooltip ?? UNSUBMITTED_CHANGES_TOOLTIP;
    } else if (rowProperties?.hasErrors) {
      return ROW_ERROR_TOOLTIP;
    }
    return '';
  }
}

export function getValidationIndicator(
  rowData: RowConstants.RowData | null | undefined,
  columnConstraint: ColumnConstraint | null
): ReactNode {
  assert(
    !(Boolean(rowData) && Boolean(columnConstraint)),
    'TableAxisCell should have rowData if it appears in the primary sample column and a columnConstraint ' +
      'if it appears in the header row, but not both at the same time'
  );
  if (rowData) {
    return rowData.uiData ? rowData.uiData.userAnnotationIndicator : null;
  }
  if (columnConstraint) {
    return columnConstraint.userAnnotationIndicator;
  }
  return null;
}

export function getCellStyle(
  isAffixed: boolean,
  affixedLeftSpacing: number,
  searchResultProps: MediocreSearchResultProps
): React.CSSProperties {
  const cellStyles: React.CSSProperties = {};
  if (isAffixed) {
    cellStyles.left = `${affixedLeftSpacing}px`;
  }
  const {results, selected} = getSearchResultsAtRoot(searchResultProps);
  if (selected) {
    cellStyles.backgroundColor = SELECTED_SEARCH_RESULT_COLOR;
  } else if (results.length > 0) {
    cellStyles.backgroundColor = SEARCH_RESULT_COLOR;
  }
  return cellStyles;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TableAxisCellComponentProps extends TableAxisCellProps {
  /**
   * The dimension actions the cell's context menu offers, which {@link TableAxisCell} narrows to
   * what the cell's model permits. Absent for an axis cell rendered outside of a table, which
   * offers none of them.
   */
  dimActions?: AxisCellDimActions;
  /**
   * The table-level context menu inputs, which {@link TableAxisCell} reads from the table model.
   * Absent for an axis cell rendered outside of a table, which no table contributes to.
   */
  contextMenuTableContext?: AxisCellContextMenuTableContext;
}

/**
 * The axis cell itself. Rendered by {@link TableAxisCell} inside a table and by
 * {@link SimpleTableAxisCell} outside one.
 */
export function TableAxisCellComponent(props: TableAxisCellComponentProps): JSX.Element {
  const axisCellModel = useAxisCellModel();
  const {
    dim,
    index,
    isGhostCell,
    customLabel,
    isHeaderCell,
    selectionRange,
    isSelected,
    showExcessLabels,
    isLeftAlign,
  } = axisCellModel;
  const {isAffixed, affixedLeftSpacing, showAffixedShadow, isLastFrozenColumn, isFirstColumn} =
    axisCellModel.affixedLayout;
  // The column-only parts of the model. Their defaults here are what the row axis renders with.
  const columnHeader = isColumnHeaderModel(axisCellModel) ? axisCellModel : null;
  const isSingleInputHeader = columnHeader?.isSingleInputHeader ?? false;
  const iconPrefix = columnHeader?.iconPrefix ?? null;
  const tooltipIcons = columnHeader?.tooltipIcons ?? null;
  const shouldHighlightCell = columnHeader?.shouldHighlightCell ?? false;
  const columnIsOutdated = columnHeader?.columnIsOutdated ?? false;
  const columnConstraint = columnHeader?.columnConstraint ?? null;
  const searchResultProps = columnHeader?.searchResultProps ?? EMPTY_SEARCH_RESULT_PROPS;
  // The row-only parts of the model. Their defaults here are what the column axis renders with.
  const rowHeader = isRowHeaderModel(axisCellModel) ? axisCellModel : null;
  const rowData = rowHeader?.rowData ?? null;
  const rowProperties = rowHeader?.rowProperties ?? null;
  const {
    dragHandleMouseDown,
    cellDragStart,
    cellDragOver,
    finalizeAndExitEditMode,
    hasRowBeenInView,
    dataTestAttrs,
  } = props;
  const showDragHandle = props.showDragHandle ?? axisCellModel.showDragHandle;

  const contextMenuButtonRef = useRef<HTMLSpanElement>(null);
  const {menuOptions, hasContextOptions} = useAxisCellContextMenu(
    props,
    props.dimActions,
    props.contextMenuTableContext
  );

  // --- Event handlers ---

  const handleCellDragStart = (e: ElementMouseEvent): void => {
    finalizeAndExitEditMode?.();

    if (cellDragStart == null) {
      return;
    }
    // Return if the button was initiated by an open contextmenu click
    const fromMenuClick = contextMenuButtonRef.current
      ? $(e.target).closest(contextMenuButtonRef.current).length !== 0
      : false;
    if ((fromMenuClick || e.button !== 0) && isInSelectionRange(selectionRange, index)) {
      return;
    }
    cellDragStart(
      {
        start: getStartIndex(dim, index),
        active: TableUtil.dimensionToSelection(dim),
      },
      e
    );
  };

  const handleCellDragOver = (e: ElementMouseEvent): void => {
    cellDragOver?.(getStartIndex(dim, index), e);
  };

  const handleOpenContextMenu = (e: BasicEvent): void => {
    if (!(selectionRange != null && hasContextOptions)) {
      e.preventDefault();
      return;
    }
    contextmenu.view.open(e, menuOptions, true);
  };

  // --- Render helpers ---

  const resolvedTooltipIcons = tooltipIcons || columnConstraint?.tooltipIcons;

  const renderIcons = (): Array<JSX.Element> | null => {
    if (resolvedTooltipIcons == null) {
      return null;
    }
    return resolvedTooltipIcons.map(({icon}, i) => (
      <span key={i} className="mediocre-tableEditable-axisCell-icon u-vertical-center-items">
        <BenchlingIcon icon={icon} />
      </span>
    ));
  };

  const tooltipTitle = getTooltipTitle(dim, columnConstraint, tooltipIcons, rowProperties);

  const renderPrefixIcon = (renderTooltip: boolean = true): JSX.Element | null => {
    let element: JSX.Element;

    if (columnConstraint?.isProtected) {
      element = (
        <span
          className={classNames('mediocre-tableEditable-axisCell-prefix-icon', {
            'mediocre-tableEditable-axisCell--highlighted-content': shouldHighlightCell,
          })}
        >
          <BenchlingIcon icon="lock" />
        </span>
      );
    } else if (BenchlingConfig.ELN_ENABLE_COLUMN_TYPE_ICONS && iconPrefix) {
      element = (
        <BenchlingIcon
          icon={iconPrefix}
          className={classNames('mediocre-tableEditable-axisCell-prefix-icon', {
            'mediocre-tableEditable-axisCell--highlighted-content': shouldHighlightCell,
            [css(aphroditeStyles.columnMappingIcon)]: iconPrefix === 'column-mapping',
          })}
        />
      );
    } else {
      return null;
    }

    if (!renderTooltip) {
      return element;
    }

    return (
      <Tooltip
        text={
          // Provide a specific div for the tooltip text to widen it slightly
          <div className="mediocre-tableEditable-axisCell-tooltip-text">{tooltipTitle}</div>
        }
        isDisabled={!tooltipTitle}
        placement={FloatingPlacement.TOP}
      >
        {element}
      </Tooltip>
    );
  };

  const renderLabel = (rowInView?: boolean): ReactNode => {
    if (rowInView != null && !rowInView) {
      // Return a skeleton chip until we're in the View and need to render the real value
      return <Chip faded={false} size={ChipSize.MINI} text="&nbsp;" />;
    }

    const hideExcess = shouldHideExcessLabels(showExcessLabels, customLabel);
    const excessLabel = getExcessLabel(dim, index);

    let visibleContent: ReactNode;
    if (hideExcess) {
      visibleContent = customLabel || '\u00a0';
    } else if (customLabel == null) {
      visibleContent = excessLabel;
    } else {
      visibleContent = (
        <>
          {excessLabel} {'\u2013'} {customLabel}
        </>
      );
    }
    return (
      <span
        className="mediocre-tableEditable-axisCell-labelInnerWrapper"
        {...getTestProps({component: 'TableAxisCell', element: 'label'})}
      >
        {visibleContent}
        {columnConstraint?.structuredTableColumn?.isRequired ? (
          <span className="u-danger-color">*</span>
        ) : null}
      </span>
    );
  };

  const renderLabelSizer = (): ReactNode => {
    // Target size is just to fit the label
    if (customLabel) {
      return customLabel;
    }
    // If there is no label, size based on the excess label
    return shouldHideExcessLabels(showExcessLabels, customLabel) ? '\u00a0' : getExcessLabel(dim, index);
  };

  const renderDragHandle = (): ReactNode => {
    if (!showDragHandle || !dragHandleMouseDown) {
      return null;
    }
    return (
      <TableAxisCellDragHandle dim={dim} onMouseDown={() => dragHandleMouseDown?.(dim, index) ?? false} />
    );
  };

  // --- Render ---

  const prefixIcon = renderPrefixIcon();
  const cellStyle = getCellStyle(isAffixed, affixedLeftSpacing, searchResultProps);

  const content = (
    <>
      {!isSingleInputHeader && renderDragHandle()}
      {getValidationIndicator(rowData, columnConstraint)}
      <div
        className={classNames('mediocre-tableEditable-axisCell-labelWrapper', {
          'is-singleInput-header': isSingleInputHeader,
        })}
      >
        {/* Render the typical label along with any icons, invisibly, to properly size the cell. The
            visible label is absolutely positioned, so the excess code reference ("A –") shown while
            editing a formula wraps within this width instead of widening the cell — matching normal
            table header axis cells (BNCH-156759). */}
        <div className="invisible">{renderPrefixIcon(false)}</div>

        <div
          className={classNames(
            'mediocre-tableEditable-axisCell-labelSizer',
            'u-overflow-wrap-break-word',
            'invisible',
            {
              'mediocre-tableEditable-axisCell--header': isHeaderCell,
            }
          )}
        >
          {renderLabelSizer()}
        </div>
        <div className="u-flex-display invisible">{renderIcons()}</div>
        {/* Depending on hover / excel-mode, render the correct label. This is absolutely positioned,
            so changing the label size does not cause any jank resizes */}
        <div
          className={classNames('mediocre-tableEditable-axisCell-label', 'u-vertical-center-items', {
            'is-singleInput-header': isSingleInputHeader,
          })}
        >
          {isSingleInputHeader && renderDragHandle()}
          {prefixIcon}
          {/* The lock glyph is decorative, so name the protected state for screen readers. */}
          {columnConstraint?.isProtected ? (
            <span className="sr-only">{LOCKED_COLUMN_DESCRIPTION}</span>
          ) : null}
          <span
            className={classNames(
              'mediocre-tableEditable-axisCell-label-text u-white-space-pre-wrap u-overflow-wrap-break-word',
              {
                'text-left': isLeftAlign,
                'mediocre-tableEditable-axisCell--header': isHeaderCell,
                'mediocre-tableEditable-axisCell--highlighted-content': shouldHighlightCell,
              }
            )}
            {...getTestProps(dataTestAttrs)}
          >
            {renderLabel(hasRowBeenInView)}
          </span>
          <div className="u-flex-display">{renderIcons()}</div>
          {hasContextOptions ? (
            <span
              ref={contextMenuButtonRef}
              className={classNames('mediocre-tableEditable-axisCell-dropdown u-vertical-center-items')}
              onClick={(e) => {
                handleOpenContextMenu(e);
              }}
              {...getTestProps({component: 'TableAxisCell', element: 'contextMenuButton'})}
            >
              <BenchlingIcon icon="chevron-down" />
            </span>
          ) : null}
        </div>
      </div>
    </>
  );

  return (
    <td
      {...getTestProps({
        component: 'TableAxisCell',
        element: dim === TABLE_ROW ? 'rowheader' : 'columnheader',
        key: `${index}`,
      })}
      aria-selected={isSelected}
      role={dim === TABLE_ROW ? 'rowheader' : 'columnheader'}
      data-column-cid={dim === TABLE_ROW ? undefined : columnConstraint?.structuredTableColumn?.cid}
      className={classNames('mediocre-tableEditable-axisCell', {
        'mediocre-tableEditable-axisCell--row': dim === TABLE_ROW,
        'mediocre-tableEditable-axisCell--col': dim === TABLE_COLUMN,
        'is-selected': isSelected,
        'is-ghost-cell': isGhostCell,
        'mediocre-table-row-status--rowHasWarnings': rowProperties?.hasWarnings,
        'mediocre-table-row-status--rowIsOutdated': rowProperties?.isOutdated,
        'mediocre-table-row-status--rowHasUnsubmittedChanges': rowProperties?.hasUnsubmittedChanges,
        'mediocre-table-row-status--rowIsTombstoned': rowProperties?.isTombstoned,
        'mediocre-table-row-status--rowHasErrors': rowProperties?.hasErrors,
        'mediocre-table-row-status--rowIsArchived':
          rowProperties?.archivePurpose != null ||
          (rowProperties?.archivePurpose != null && rowProperties?.hasErrors),
        'mediocre-tableEditable-axisCell--columnIsOutdated': columnIsOutdated,
        'mediocre-tableEditable-axisCell--highlighted-background': shouldHighlightCell,
        'cell-sticky': isAffixed,
        'cell-rightShadow': showAffixedShadow,
        'cell-freezeBorder': isLastFrozenColumn,
        'cell-isFirstColumn': isFirstColumn,
        'cell-isSingleInputHeader': isSingleInputHeader,
        'cell-isSingleInputHeader-is-selected': isSingleInputHeader && isSelected,
      })}
      onMouseDown={handleCellDragStart}
      onMouseOver={handleCellDragOver}
      style={cellStyle}
      onContextMenu={(e) => {
        handleOpenContextMenu(e);
      }}
      data-amp-safe={false}
    >
      {prefixIcon != null && content}
      {prefixIcon == null && (
        // There is no tooltip on this cell's icon, so show it on the axis cell.
        // This can happen if column type icons are not enabled or if this is a row axis cell.
        <Tooltip
          text={
            // Provide a specific div for the tooltip text to widen it slightly
            <div className="mediocre-tableEditable-axisCell-tooltip-text">{tooltipTitle}</div>
          }
          isDisabled={!tooltipTitle}
          placement={FloatingPlacement.TOP}
          toggleDisplayMode={ToggleDisplayMode.BLOCK}
        >
          {content}
        </Tooltip>
      )}
    </td>
  );
}

/**
 * The dimension actions a row axis cell may perform, narrowed to what its own model permits.
 */
export function getPermittedRowDimActions(
  rowHeader: RowHeaderModel,
  mutations: TableMutationMethods
): AxisCellDimActions {
  const {canToggleHeader, canInsertDims, canDeleteDims, canDeleteTable} = rowHeader;
  return {
    toggleHeader: canToggleHeader ? mutations.toggleHeaderRow : undefined,
    delDim: canDeleteDims ? mutations.deleteRows : undefined,
    insertDimBefore: canInsertDims ? mutations.insertRowsAbove : undefined,
    insertDimAfter: canInsertDims ? mutations.insertRowsBelow : undefined,
    deleteTable: canDeleteTable ? mutations.deleteTable : undefined,
  };
}

/**
 * The dimension actions a column axis cell may perform, narrowed to what its own model permits.
 */
export function getPermittedColumnDimActions(
  columnHeader: ColumnHeaderModel,
  mutations: TableMutationMethods
): AxisCellDimActions {
  const {canRenameDim, canToggleHeader, canInsertDims, canDeleteDims, canDeleteTable} = columnHeader;
  return {
    renameDim: canRenameDim ? mutations.renameColumn : undefined,
    toggleHeader: canToggleHeader ? mutations.toggleHeaderCol : undefined,
    delDim: canDeleteDims ? mutations.deleteColumns : undefined,
    insertDimBefore: canInsertDims ? mutations.insertColumnsLeft : undefined,
    insertDimAfter: canInsertDims ? mutations.insertColumnsRight : undefined,
    deleteTable: canDeleteTable ? mutations.deleteTable : undefined,
  };
}

/**
 * An axis cell rendered inside a table, on either axis. It takes the dimension actions it offers
 * from `useTableMutations` here, narrowed to what its own model permits, rather than having them
 * threaded down from `TableRow` / `TableHeaderRow`.
 */
function TableAxisCell(props: TableAxisCellProps): JSX.Element {
  countTableRender('TableAxisCell');
  const axisCellModel = useAxisCellModel();
  const mutations = useTableMutations();
  const dimActions = isRowHeaderModel(axisCellModel)
    ? getPermittedRowDimActions(axisCellModel, mutations)
    : getPermittedColumnDimActions(axisCellModel, mutations);
  const contextMenuTableContext = useAxisCellContextMenuTableContext(axisCellModel, mutations);

  return (
    <TableAxisCellComponent
      {...props}
      dimActions={dimActions}
      contextMenuTableContext={contextMenuTableContext}
    />
  );
}

export interface SimpleTableAxisCellProps {
  index: number;
  // Should be either 'column' or 'row'
  dim: AxisType;
  iconPrefix?: string;
  label: string;
  // The number of rows or columns that are present
  numDims: number;
  tooltipIcons?: Array<TooltipIcon>;
  isLeftAlign: boolean;
}

/**
 * A simple TableAxisCell that does not have a ColumnConstraint. It is rendered outside of any table,
 * so it offers none of the dimension actions.
 */
export function SimpleTableAxisCell(props: SimpleTableAxisCellProps): JSX.Element {
  return (
    <StandaloneAxisCellModelProvider
      dim={props.dim}
      index={props.index}
      customLabel={props.label}
      label={props.label}
      tooltipIcons={props.tooltipIcons}
      numDims={props.numDims}
      isLeftAlign={props.isLeftAlign}
    >
      <TableAxisCellComponent dataTestAttrs={{component: 'SimpleTableAxisCell', element: 'label'}} />
    </StandaloneAxisCellModelProvider>
  );
}

const aphroditeStyles = StyleSheet.create({
  columnMappingIcon: {
    color: BLUE_LIGHT,
  },
});

export default React.memo(TableAxisCell);
